import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Chip,
  TextField,
  InputAdornment,
  Badge,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Description,
  Terminal,
  Security,
  MenuBook,
  Build,
  School,
  ChevronRight,
  Search,
  Clear,
  GitHub,
  Forum,
  BugReport,
  Lightbulb,
  FiberNew,
} from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getAllDocumentationSections } from '../data/documentation';

const DocumentationSidebar = () => {
  const { section: currentSection, page: currentPage } = useParams();
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    [currentSection]: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSections, setFilteredSections] = useState([]);
  
  const docSections = React.useMemo(() => getAllDocumentationSections(), []);
  
  // Filter sections and pages based on search
  useEffect(() => {
    if (!searchTerm) {
      setFilteredSections(docSections);
      return;
    }
    
    const filtered = docSections.map(section => {
      const matchingPages = section.pages.filter(page => {
        const pageTitle = page.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        return pageTitle.toLowerCase().includes(searchTerm.toLowerCase());
      });
      
      const sectionMatches = section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            section.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (sectionMatches || matchingPages.length > 0) {
        return {
          ...section,
          pages: sectionMatches ? section.pages : matchingPages,
          isFiltered: !sectionMatches && matchingPages.length > 0
        };
      }
      return null;
    }).filter(Boolean);
    
    setFilteredSections(filtered);
    
    // Auto-expand sections with matches
    if (filtered.length > 0) {
      const expandAll = {};
      filtered.forEach(section => {
        expandAll[section.key] = true;
      });
      setExpandedSections(prev => ({ ...prev, ...expandAll }));
    }
  }, [searchTerm, docSections]);

  const handleSectionToggle = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const getSectionIcon = (key) => {
    const iconMap = {
      'getting-started': <Description color="primary" />,
      'cli': <Terminal color="primary" />,
      'security': <Security color="primary" />,
      'user-guide': <MenuBook color="primary" />,
      'developers': <Build color="primary" />,
      'tutorials': <School color="primary" />,
    };
    return iconMap[key] || <MenuBook color="primary" />;
  };

  const formatPageTitle = (page) => {
    return page.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <Box 
      sx={{ 
        height: '100%',
        backgroundColor: 'transparent',
        p: 0,
      }}
    >
      {/* Enhanced Header */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid', 
        borderColor: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.1)' 
          : 'rgba(0, 0, 0, 0.08)',
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.2)' 
          : 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(5px)',
      }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>
            Documentation
          </Typography>
          <Chip 
            label="Latest" 
            color="primary" 
            size="small" 
            icon={<FiberNew />}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Complete guide and API reference
        </Typography>
        
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search docs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={clearSearch}
                  sx={{ mr: -1 }}
                >
                  <Clear sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </Box>

      <List sx={{ 
        p: 0, 
        backgroundColor: 'transparent',
        '& .MuiListItem-root': {
          backgroundColor: 'transparent',
        },
      }}>
        {filteredSections.length === 0 && searchTerm && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No documentation found for "{searchTerm}"
            </Typography>
          </Box>
        )}
        
        {filteredSections.map((section) => {
          const isExpanded = expandedSections[section.key];
          const isCurrentSection = currentSection === section.key;
          const hasFilteredResults = section.isFiltered;
          
          return (
            <Box key={section.key}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleSectionToggle(section.key)}
                  sx={{
                    py: 1.5,
                    px: 3,
                    backgroundColor: isCurrentSection 
                      ? alpha(theme.palette.primary.main, 0.15)
                      : hasFilteredResults 
                        ? alpha(theme.palette.warning.main, 0.08)
                        : 'transparent',
                    color: isCurrentSection ? 'primary.main' : 'text.primary',
                    borderLeft: isCurrentSection ? '4px solid' : hasFilteredResults ? '3px solid' : '1px solid transparent',
                    borderColor: isCurrentSection ? 'primary.main' : hasFilteredResults ? 'warning.main' : 'transparent',
                    borderRadius: '0 8px 8px 0',
                    mx: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isCurrentSection 
                        ? alpha(theme.palette.primary.main, 0.2)
                        : hasFilteredResults 
                          ? alpha(theme.palette.warning.main, 0.12)
                          : alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: 40,
                    color: isCurrentSection ? 'primary.contrastText' : 'inherit'
                  }}>
                    {getSectionIcon(section.key)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          sx={{
                            fontWeight: isCurrentSection ? 600 : 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {section.title}
                        </Typography>
                        {hasFilteredResults && (
                          <Badge 
                            badgeContent={section.pages.length} 
                            color="warning" 
                            sx={{ '& .MuiBadge-badge': { fontSize: '0.625rem', height: 16, minWidth: 16 } }}
                          />
                        )}
                      </Stack>
                    }
                  />
                  {section.pages.length > 0 && (
                    isExpanded ? 
                      <ExpandLess sx={{ color: isCurrentSection ? 'primary.contrastText' : 'text.secondary' }} /> : 
                      <ExpandMore sx={{ color: isCurrentSection ? 'primary.contrastText' : 'text.secondary' }} />
                  )}
                </ListItemButton>
              </ListItem>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.pages.map((page) => {
                    const isCurrentPage = currentSection === section.key && currentPage === page;
                    
                    return (
                      <ListItem key={page} disablePadding>
                        <ListItemButton
                          component={RouterLink}
                          to={`/docs/${section.key}/${page}`}
                          sx={{
                            pl: 5,
                            py: 1,
                            mx: 2,
                            backgroundColor: isCurrentPage ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                            borderLeft: isCurrentPage ? '3px solid' : '1px solid transparent',
                            borderColor: isCurrentPage ? 'primary.main' : 'transparent',
                            borderRadius: '0 6px 6px 0',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              transform: 'translateX(2px)',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <ChevronRight 
                              sx={{ 
                                fontSize: 16, 
                                color: isCurrentPage ? 'primary.main' : 'text.secondary' 
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={formatPageTitle(page)}
                            primaryTypographyProps={{
                              fontSize: '0.813rem',
                              fontWeight: isCurrentPage ? 600 : 400,
                              color: isCurrentPage ? 'primary.main' : 'text.secondary',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      <Box sx={{ 
        borderTop: '1px solid',
        borderColor: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.1)' 
          : 'rgba(0, 0, 0, 0.08)',
        mt: 2,
      }} />
      
      {/* Enhanced Quick Links */}
      <Box sx={{ 
        p: 3,
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.2)' 
          : 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(5px)',
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
          Quick Links
        </Typography>
        <Stack spacing={1.5}>
          <Tooltip title="View source code and contribute" placement="right">
            <Chip
              icon={<GitHub />}
              label="GitHub Repository"
              component="a"
              href="https://github.com/KiefStudioMA/PWGen-Rust"
              target="_blank"
              clickable
              size="medium"
              variant="outlined"
              sx={{ 
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'primary.main',
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Report bugs and request features" placement="right">
            <Chip
              icon={<BugReport />}
              label="Report Issue"
              component="a"
              href="https://github.com/KiefStudioMA/PWGen-Rust/issues"
              target="_blank"
              clickable
              size="medium"
              variant="outlined"
              sx={{ 
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'error.light',
                  borderColor: 'error.main',
                  color: 'error.contrastText',
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Join community discussions" placement="right">
            <Chip
              icon={<Forum />}
              label="Discussions"
              component="a"
              href="https://github.com/KiefStudioMA/PWGen-Rust/discussions"
              target="_blank"
              clickable
              size="medium"
              variant="outlined"
              sx={{ 
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'success.light',
                  borderColor: 'success.main',
                  color: 'success.contrastText',
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Suggest improvements" placement="right">
            <Chip
              icon={<Lightbulb />}
              label="Feature Requests"
              component="a"
              href="https://github.com/KiefStudioMA/PWGen-Rust/discussions/categories/ideas"
              target="_blank"
              clickable
              size="medium"
              variant="outlined"
              sx={{ 
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'warning.light',
                  borderColor: 'warning.main',
                  color: 'warning.contrastText',
                }
              }}
            />
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};

export default DocumentationSidebar;